import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react';

import { apiClient } from '@/shared/lib/axios';
import {
  claimActiveSessionSyncReload,
  clearActiveSessionSyncReload,
  captureActiveSessionAuthGeneration,
  createActiveSessionSyncTrigger,
  isActiveSessionAuthGenerationCurrent,
  isActiveSessionSyncReloadCurrent,
  markActiveSessionSyncReloadRedirecting,
  readActiveSessionSyncReload,
  resolveActiveSessionSyncConfig,
  runWithActiveSessionSyncTransition,
  settleActiveSessionSyncReload,
  subscribeActiveSessionSyncReload,
} from './activeSessionSync';
import {
  createUnifiedAbandonedMutationRecovery,
  runP1SsoAuthMutation,
} from './ssoAuthMutationLock';
import { hideSsoSyncCurtain, readSsoSyncCurtainRevision } from './ssoSyncCurtain';
import type { SsoContext } from './ssoSession';

const mountedContexts = new Map<string, Set<symbol>>();
const contextKey = (context: SsoContext): string => `${context.site}:${context.entryType}`;
const hasMountedContext = (context: SsoContext): boolean =>
  (mountedContexts.get(contextKey(context))?.size ?? 0) > 0;
const serverReloadSnapshot = () => undefined;

// The session sync runs only after an explicit browser reload (F5/reload button), and exactly once
// for that boot. It must not run on ordinary navigation, mount, focus or visibility changes because
// the broker round trip may replace the page. Login-page passive SSO and explicit login/logout are
// separate flows and remain unchanged. The entry module arms one exact context only when that
// reload already had an authenticated session at boot.

export const useActiveSessionSync = (
  context: SsoContext | undefined,
  authenticated: boolean,
): boolean => {
  const site = context?.site;
  const entryType = context?.entryType;
  const config = resolveActiveSessionSyncConfig(
    import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED,
    import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_COOLDOWN_MS,
  );
  const reload = useSyncExternalStore(
    subscribeActiveSessionSyncReload,
    readActiveSessionSyncReload,
    serverReloadSnapshot,
  );
  useEffect(() => {
    if (
      site === undefined ||
      entryType === undefined ||
      config === undefined
    ) {
      return;
    }

    const activeContext: SsoContext = { site, entryType };
    const intent = readActiveSessionSyncReload();
    const curtainRevision = readSsoSyncCurtainRevision();
    const owner = Symbol('active-sync-owner');
    const key = contextKey(activeContext);
    const owners = mountedContexts.get(key) ?? new Set<symbol>();
    owners.add(owner);
    mountedContexts.set(key, owners);
    const release = () => {
      owners.delete(owner);
      if (owners.size === 0) mountedContexts.delete(key);
      // StrictMode reattaches synchronously. A real route/context unmount
      // abandons only its own boot; no response from it may navigate later.
      queueMicrotask(() => {
        const current = readActiveSessionSyncReload();
        if (!intent || intent.context.site !== site || !current
          || !isActiveSessionSyncReloadCurrent(intent) || hasMountedContext(intent.context)
          || hasMountedContext(activeContext)
          || current.phase === 'redirecting') return;
        if (clearActiveSessionSyncReload(intent)) hideSsoSyncCurtain(curtainRevision);
      });
    };
    if (!authenticated || !intent || !claimActiveSessionSyncReload(activeContext, curtainRevision)) {
      return release;
    }
    let generation: ReturnType<typeof captureActiveSessionAuthGeneration>;
    let localStorage: Storage;
    let sessionStorage: Storage;
    try {
      localStorage = window.localStorage;
      sessionStorage = window.sessionStorage;
      generation = captureActiveSessionAuthGeneration(activeContext, localStorage);
    } catch {
      settleActiveSessionSyncReload(intent);
      return release;
    }
    // The curtain is raised in the entry module, before anything paints. It belongs to the
    // navigation it was raised for, so anything short of that navigation must reveal the app.
    let navigated = false;
    const isCurrent = () => isActiveSessionSyncReloadCurrent(intent)
      && hasMountedContext(activeContext)
      && isActiveSessionAuthGenerationCurrent(generation, localStorage);
    const activeSyncTrigger = createActiveSessionSyncTrigger({
      context: activeContext,
      cooldownMs: config.cooldownMs,
      storage: sessionStorage,
      authGenerationStorage: localStorage,
      location: () => ({
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        // Carried so the redirect guard can tell a downgrade from a deployment that simply has
        // no certificate. Without it an http-only environment reached by IP:port never follows
        // the broker URL, and the sync fails silently.
        protocol: window.location.protocol,
      }),
      now: Date.now,
      request: (endpoint, payload) => runWithActiveSessionSyncTransition(async () => {
        const response = await apiClient.post(endpoint, payload, {
          withCredentials: true,
        });
        return response.data?.data;
      }),
      navigate: redirectUrl => {
        if (!isCurrent()) return;
        window.location.replace(redirectUrl);
        navigated = true;
        markActiveSessionSyncReloadRedirecting(intent);
      },
      userReload: true,
      isCurrent,
    });

    void runP1SsoAuthMutation({
      context: activeContext,
      ssoEnabled: true,
      storage: localStorage,
      canRecoverAbandonedMutation: createUnifiedAbandonedMutationRecovery({
        context: activeContext,
        storage: localStorage,
        sessionStorage,
        isCurrent,
      }),
      operation: () => isActiveSessionSyncReloadCurrent(intent) && hasMountedContext(activeContext)
        ? activeSyncTrigger()
        : Promise.resolve(),
    }).finally(() => {
      if (!navigated) settleActiveSessionSyncReload(intent);
    });
    return release;
  }, [authenticated, config?.cooldownMs, entryType, site]);

  useLayoutEffect(() => {
    if (reload?.phase !== 'settled' || reload.context.site !== site
      || reload.context.entryType !== entryType) return;
    // Children have committed before this layout effect. Revealing the page
    // earlier would trade the duplicate requests for a blank intermediate frame.
    hideSsoSyncCurtain(reload.curtainRevision);
    clearActiveSessionSyncReload(reload);
  }, [entryType, reload, site]);

  return Boolean(authenticated && config && reload
    && reload.phase !== 'settled'
    && reload.context.site === site && reload.context.entryType === entryType);
};
